import { isDate } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { TimePickerValue } from "react-time-picker";
dayjs.extend(utc);

const dateHelper = {
  /** Get the current UTC date (to take out timezones from times) */
  getCurrentDateUtc: function (): Date {
    return dayjs().utc().toDate();
  },
  /** Gets the current UTC date minus or plus the days from given */
  getDaysFromCurrentDateUtcs: function (daysFrom: number): Date {
    return dayjs.utc().add(daysFrom, "day").toDate();
  },
  /** Gets the current UTC date minus or plus the duration given */
  getFromCurrentDateUtcs: function (daysFrom: number, duration: string): Date {
    return dayjs.utc().add(daysFrom, duration).toDate();
  },
  /** Reinterprets a local date/time's calendar and clock components as UTC.
   * This changes the represented instant; do not use it for genuine timestamps.
   * Do not use for date-only picker values such as an advanced task TargetDate:
   * JSON serialization can then move the selected calendar day backwards.
   */
  convertDateToUtc: function (localDate: Date): Date {
    // Format the local date into the value that was chosen
    const formattedLocalDate = dayjs(localDate).format("YYYY-MM-DDTHH:mm:ss");
    // Construct a new UTC date which matches the input date/time
    const utcDate = dayjs(formattedLocalDate).utc();
    //console.log("ISO date check", JSON.stringify({originalUtcDate: localDate, editedUtcDate: utcDate.toDate()}));
    return utcDate.toDate();
  },

  /** Parses an API value whose date/time components are expressed in UTC. */
  convertDateFromStringToUtc: function (inputDate: string | Date): Date {
    return dayjs.utc(inputDate).toDate();
  },

  /** Converts a string to a date*/
  convertDateFromString: function (
    inputDate: string | Date | null | undefined
  ): Date | null {
    if (!inputDate) return null;
    return dayjs(inputDate).toDate();
  },

  /** Converts a UTC instant to local time for display. */
  convertUtcDateToLocal: function (utcDate: Date): Date {
    const dateObj = dayjs.utc(utcDate);
    if (dateObj.isUTC()) {
      return dateObj.local().toDate();
    }
    return dateObj.toDate();
  },

  /** Represents a locally selected calendar day as midnight UTC.
   * Use immediately before serializing a date-only field whose API type is DateTime,
   * such as an advanced task TargetDate. This preserves the day selected in the picker.
   * Do not use for timestamps such as DateCreated or DateLastModified.
   */
  localCalendarDateAsUtcMidnight: function (date: Date): Date {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
  },

  /** Represents a UTC calendar day as local midnight for pickers and display.
   * Inverse of localCalendarDateAsUtcMidnight: use when reading a date-only field
   * stored as midnight UTC so west-of-UTC browsers still show the stored day.
   */
  utcCalendarDateAsLocalMidnight: function (date: Date): Date {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  },

  /** Maps a stored advanced-task TargetDate for pickers and display.
   * Midnight UTC (post-fix storage) uses the UTC calendar day as local midnight.
   * Legacy non-midnight values keep UTC parsing so EU completed tasks still display
   * as they did before the calendar-day backfill.
   */
  storedTargetDateAsLocalCalendarDate: function (
    inputDate: string | Date
  ): Date {
    const utcDate = dayjs.utc(inputDate).toDate();
    if (
      utcDate.getUTCHours() === 0 &&
      utcDate.getUTCMinutes() === 0 &&
      utcDate.getUTCSeconds() === 0 &&
      utcDate.getUTCMilliseconds() === 0
    ) {
      return this.utcCalendarDateAsLocalMidnight(utcDate);
    }
    return this.convertUtcDateToLocal(utcDate);
  },

  /** Converts a date object into yyyy-MM-dd ready for sending to the API.
   * Use for date-only picker values when the API property is a yyyy-MM-dd string.
   * For a DateTime API property, use localCalendarDateAsUtcMidnight instead.
   */
  convertDateObjectToServerString: function (date: Date | null): string | null {
    if (!date || !isDate(date)) return null;

    // Pad the date with zeros, e.g. for 1st Jan 2021, we want 2021-01-01, not 2021-1-1
    const zeroPad = (num: number, places: number) =>
      String(num).padStart(places, "0");

    return (
      date.getFullYear() +
      "-" +
      zeroPad(date.getMonth() + 1, 2) +
      "-" +
      zeroPad(date.getDate(), 2)
    );
  },

  /** Converts Date into LocaleDateString for catch ups (this includes timezone) */
  convertLocaleDateStringForCatchUps: function (date: Date): string {
    const localeDateString = date.toLocaleString([], {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12",
    });

    const timeZoneAbbrev = this.getTimeZoneName(date, "short"); // e.g., "PDT" or "BST"
    return `${localeDateString} (${timeZoneAbbrev})`;
  },
  getTimeZoneName(
    date: Date,
    timeZoneStyle: "short" | "long" = "short"
  ): string {
    // Safe to use 'en-US' here as it's only for providing consistent formatting for the timezone name
    // and not for displaying the date/time itself.
    const formatter = new Intl.DateTimeFormat([], {
      timeZoneName: timeZoneStyle,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    const parts = formatter.formatToParts(date);

    const timeZonePart = parts.find((part) => part.type === "timeZoneName");
    return timeZonePart?.value ?? "local time";
  },

  /** Validates a time value */
  timeIsValid: function (
    inputValue: string | TimePickerValue | undefined
  ): boolean {
    if (inputValue === undefined || inputValue === null) return false;
    if (
      inputValue.toString().length === 5 &&
      inputValue.toString().charAt(2) === ":"
    )
      return true;
    return false;
  },
  /** Takes a selected date and time, and merges them into one object */
  combineDateAndTime: function (
    inputDate: Date,
    inputTime: string | TimePickerValue
  ): Date | null {
    if (!inputDate || !inputTime || inputTime.toString().length === 0)
      return null;

    if (!this.timeIsValid(inputTime)) return null;

    const timeParts = inputTime.toString().split(":");
    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    const output = new Date(inputDate);
    output.setHours(hours);
    output.setMinutes(minutes);
    output.setSeconds(0);
    return output;
  },
};

export function formatDistanceToNow(utcDateString: string): string {
  const date = dayjs.utc(utcDateString);
  const now = dayjs.utc();
  const diffMinutes = now.diff(date, "minute");

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  const diffHours = now.diff(date, "hour");
  if (diffHours < 24) return `${diffHours} hours ago`;
  const diffDays = now.diff(date, "day");
  return `${diffDays} days ago`;
}

export default dateHelper;
